// packages import
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  FormControlLabel,
  Link,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from '@mui/material'
import { FC, useState, MouseEvent, useContext } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { KeyboardArrowDown } from '@mui/icons-material'

// component import
import { ContentHead } from '../../../components/common/ContentHead'
import { EmptyData } from '../../../components/common/EmptyData'
import { FinalBillsListing } from '../../../components/billing/finalBills/FinalBillsListing'
import FinalBillsFilters from '../../../components/billing/finalBills/filters'
import { CustomModal } from '../../../components/common/CustomModal'

// others
import { CardBox } from '../../../theme/styleComponents'
import { flexCenter } from '../../../theme/styleConstants'
import { FinalBillsContext } from '../../../context/FinalBillsContext'
import { ConfirmationModal } from '../../../components/common/ConfirmationModal'
import {
  BillBulkAction,
  BillStatus,
  BulkActionResult,
  useBillBulkActionsMutation,
  useMarkBillsAsDeliveredMutation
} from '../../../../generated'
import { Alert } from '../../../components/common/Alert'
import { AuthContext } from '../../../context'
import { printBills, printBillsV2 } from '../../../../services'
import { canVoidBill } from '../../../utils/billing'
import { hasAdminAccess } from '../../../utils'
import { SelectedBills } from '../../../interfaceTypes'

const PRINT_SELECTION_LIMIT = 5

/**
 * Bills that can be marked as delivered — a bill has to have actually been issued to a client.
 * Mirrors `canMarkDelivered` on the row-level menu and the server's own allow-list, so the same
 * bill is markable from the row action, this bulk action, and the print job's "Mark bills as
 * delivered".
 */
const DELIVERABLE_BILL_STATUSES = [
  BillStatus.Billed,
  BillStatus.SentToClient,
  BillStatus.PartiallyPaid,
  BillStatus.Paid,
  BillStatus.Forwarded
]

export const FinalBills: FC = (): JSX.Element => {
  const { bills, loading, refetch, setFilters, filters } = useContext(FinalBillsContext)
  const { currentUser } = useContext(AuthContext)
  const [bulkActionAnchorEl, setBulkActionAnchorEl] = useState<null | HTMLElement>(null)
  const [showVoidConfirmation, setShowVoidConfirmation] = useState(false)
  const [showPrintConfirm, setShowPrintConfirm] = useState(false)
  const [showPrintLimitModal, setShowPrintLimitModal] = useState(false)
  const [showMarkDeliveredConfirm, setShowMarkDeliveredConfirm] = useState(false)
  const [selectedFinalBills, setSelectedFinalBills] = useState<SelectedBills>({
    selectedIds: [],
    allSelected: false
  })

  const bulkActionOpen = Boolean(bulkActionAnchorEl)
  const isEmpty = bills?.length === 0 && !loading
  const selectedBillIds = selectedFinalBills.selectedIds
  const allBillsSelected = selectedFinalBills.allSelected

  const onCompletedBulk = (successMessage: string, results: BulkActionResult[]) => {
    const hasError = results.filter(result => !result.success)
    if (!hasError.length) {
      Alert.success(successMessage)
      refetch()
    } else
      hasError.forEach(failedBill => {
        Alert.error(`Bill ${failedBill.billId} failed due to ${failedBill.error}`)
      })
  }

  const [billBulkActions, { loading: performingAction }] = useBillBulkActionsMutation({
    onError: error => {
      console.log('Error in sending bills for approval', error)
      Alert.error(error.message)
    },
    onCompleted: data => {
      const { billBulkActions } = data || {}
      const { results, response } = billBulkActions || {}
      const { message } = response || {}
      onCompletedBulk(message || 'Done!', results)
    }
  })

  const [markBillsAsDelivered, { loading: markingDelivered }] = useMarkBillsAsDeliveredMutation({
    onError: error => Alert.error(error.message),
    onCompleted: data => {
      const { markedBillIds, skippedBillIds, response } = data?.markBillsAsDelivered || {}
      Alert.success(response?.message || 'Bills marked as delivered to client')
      // The server skips anything that was never issued to a client; name those bills rather than
      // quietly reporting a smaller number than the user selected.
      if (skippedBillIds?.length) {
        Alert.error(`Skipped ${skippedBillIds.length} bill(s) that cannot be delivered: ${skippedBillIds.join(', ')}`)
      }
      if (markedBillIds?.length) refetch()
      setShowMarkDeliveredConfirm(false)
      setSelectedFinalBills({ selectedIds: [], allSelected: false })
    }
  })

  /** Bills the bulk action applies to: the whole loaded page when "select all" is on. */
  const getTargetBills = () =>
    allBillsSelected ? bills ?? [] : (bills ?? []).filter(bill => selectedBillIds?.includes(bill.id))

  const handleBulkActionClick = (event: MouseEvent<HTMLButtonElement>) => {
    setBulkActionAnchorEl(event.currentTarget)
  }

  const handleBulkActionClose = () => {
    setBulkActionAnchorEl(null)
  }
  const handleCloseVoidConfirmation = () => setShowVoidConfirmation(false)

  const handleBulkVoid = async () => {
    const billIds = allBillsSelected && bills?.length ? bills.map(b => b.id) : selectedBillIds
    await billBulkActions({
      variables: {
        bulkInput: {
          billIds,
          bulkActionType: BillBulkAction.VoidBills
        }
      }
    })
    handleCloseVoidConfirmation()
    handleBulkActionClose()
    setSelectedFinalBills({ selectedIds: [], allSelected: false })
  }

  const handleBulkReminder = async () => {
    await billBulkActions({
      variables: {
        bulkInput: {
          billIds: selectedBillIds,
          bulkActionType: BillBulkAction.SendReminderToClients
        }
      }
    })
    handleBulkActionClose()
  }

  const handleBulkSendToClient = async () => {
    await billBulkActions({
      variables: {
        bulkInput: {
          billIds: selectedBillIds,
          bulkActionType: BillBulkAction.SendBillsToClient
        }
      }
    })
    handleBulkActionClose()
  }

  const handleBulkPrint = async () => {
    await printBills(selectedBillIds)

    handleBulkActionClose()
  }

  const handleBulkPrintV2 = async () => {
    const rawIds = allBillsSelected && bills?.length ? bills.map(b => b.id) : selectedBillIds
    const billIdsToPrint = rawIds?.map(id => (typeof id === 'number' ? id : Number(id))).filter(Number.isFinite) ?? []
    if (!billIdsToPrint.length) return
    try {
      await printBillsV2(billIdsToPrint, { markAsDelivered: true })
      handleBulkActionClose()
      // Printing marks the issued bills as delivered; refetch so "Sent to Client" reflects it.
      refetch()
    } catch (e) {
      Alert.error(e instanceof Error ? e.message : 'Failed to generate PDF')
    }
  }

  const handleMarkDeliveredClick = () => {
    handleBulkActionClose()
    setShowMarkDeliveredConfirm(true)
  }

  const handleBulkMarkDelivered = async () => {
    const billIds = getTargetBills().map(bill => Number(bill.id))
    if (!billIds.length) return
    await markBillsAsDelivered({ variables: { billIds } })
  }

  const handlePrintClick = () => {
    handleBulkActionClose()
    const count = allBillsSelected ? bills?.length ?? 0 : selectedBillIds.length
    if (count > PRINT_SELECTION_LIMIT) {
      setShowPrintLimitModal(true)
    } else {
      setShowPrintConfirm(true)
    }
  }

  const showVoid = () => {
    const commonBills = allBillsSelected ? bills : bills.filter(bill => selectedBillIds?.includes(bill.id))
    return commonBills.every(bill => canVoidBill(currentUser, bill?.status, bill?.amountPaid))
  }

  const showReminder = () => {
    const commonBills = allBillsSelected ? bills : bills.filter(bill => selectedBillIds?.includes(bill.id))
    return commonBills.every(
      bill => bill.sendToClient && (bill.status === BillStatus.PartiallyPaid || bill.status === BillStatus.SentToClient)
    )
  }

  const showSendToClient = () => {
    const commonBills = allBillsSelected ? bills : bills.filter(bill => selectedBillIds?.includes(bill.id))
    return commonBills.every(bill => bill.status === BillStatus.Billed && bill.sendToClient)
  }

  /**
   * Shown when at least one selected bill is deliverable — unlike Void, which needs every bill to
   * qualify. A mixed selection is normal here (the page lists pre-bills and voided bills alongside
   * issued ones), and hiding the action outright would leave no way to tell why. The confirmation
   * states exactly how many will be marked and how many skipped instead.
   */
  const showMarkDelivered = () => getTargetBills().some(bill => DELIVERABLE_BILL_STATUSES.includes(bill.status))

  const deliverableCount = getTargetBills().filter(bill => DELIVERABLE_BILL_STATUSES.includes(bill.status)).length
  const notDeliverableCount = getTargetBills().length - deliverableCount

  const showReminderItem = hasAdminAccess(currentUser) && showReminder()
  const showVoidItem = hasAdminAccess(currentUser) && showVoid()
  const showSendToClientItem = hasAdminAccess(currentUser) && showSendToClient()
  const showMarkDeliveredItem = hasAdminAccess(currentUser) && showMarkDelivered()

  const renderActionButtons = () => (
    <Box display="flex" gap={2} alignItems="center">
      {/* Same rule as the Delinquent Bills print job — this checkbox is how a biller previews what
          that job will produce, so the tooltip has to state every condition. */}
      <Tooltip title="Flat fee matters carrying an unpaid balance more than 30 days old, including balances carried forward from earlier bills, where none of those aged bills has received any payment at all. Matters still inside the 30-day grace period, matters on any other billing type, and matters that have paid something against their aged bills are not shown. Collection matters (Resp1 Attorney Coll1 or Coll2) are not shown either, unless the Attorney filter is set to Coll1 or Coll2.">
        <FormControlLabel
          control={<Checkbox checked={filters.isDelinquentBills} />}
          label="Delinquent Bills"
          onChange={() => setFilters({ ...filters, isDelinquentBills: !filters.isDelinquentBills })}
        />
      </Tooltip>

      {canPerformBulkAction && (
        <>
          <Button
            variant="outlined"
            endIcon={<KeyboardArrowDown />}
            onClick={handleBulkActionClick}
            disabled={!selectedBillIds?.length && !allBillsSelected}
          >
            Bulk Action
          </Button>
          <Menu anchorEl={bulkActionAnchorEl} open={bulkActionOpen} onClose={handleBulkActionClose}>
            {/* <MenuItem onClick={handleBulkPrint}>Print</MenuItem> */}
            <MenuItem onClick={handlePrintClick}>Print</MenuItem>

            {/* Disabling this option until we have the complete information on its flow */}
            {/* {showReminderItem && <MenuItem onClick={handleBulkReminder}>Send Reminder</MenuItem>} */}
            {showVoidItem && <MenuItem onClick={() => setShowVoidConfirmation(true)}>Void</MenuItem>}

            {showMarkDeliveredItem && (
              <MenuItem onClick={handleMarkDeliveredClick} disabled={markingDelivered}>
                Mark as Delivered
              </MenuItem>
            )}

            {/* Disabling this option until we have the complete information on its flow */}
            {/* {showSendToClientItem && <MenuItem onClick={handleBulkSendToClient}>Send To Client</MenuItem>} */}
          </Menu>
        </>
      )}
    </Box>
  )

  const canPerformBulkAction = hasAdminAccess(currentUser)

  return (
    <CardBox marginTop={3}>
      <ContentHead title="Bills" customButton={renderActionButtons} />

      <FinalBillsFilters />

      {isEmpty ? (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
          <EmptyData title="No bills found" />
        </Box>
      ) : (
        <FinalBillsListing
          bills={bills}
          loading={loading}
          refetch={refetch}
          selectedFinalBills={selectedFinalBills}
          setSelectedFinalBills={setSelectedFinalBills}
        />
      )}

      <ConfirmationModal
        isOpen={showMarkDeliveredConfirm}
        title="Mark Bills as Delivered"
        message={
          `Confirm that ${deliverableCount} selected bill(s) have been delivered to the client.\n\n` +
          'Each one is marked as sent to the client - its "Sent to Client" flag is set to Yes and the sent date ' +
          'is recorded. This cannot be undone.' +
          (notDeliverableCount
            ? `\n\n${notDeliverableCount} of the selected bill(s) will be skipped — only billed, sent, partially ` +
              'paid, paid or forwarded bills can be marked as delivered.'
            : '')
        }
        confirmText="Mark as Delivered"
        cancelText="Cancel"
        variant="danger"
        isLoading={markingDelivered}
        onConfirm={handleBulkMarkDelivered}
        onCancel={() => setShowMarkDeliveredConfirm(false)}
      />

      <ConfirmationModal
        isOpen={showPrintConfirm}
        title="Print Bills"
        message={`This will generate a PDF for the selected ${
          allBillsSelected ? 'bills' : `${selectedBillIds.length} bill(s)`
        }. Issued bills not yet delivered will be marked as delivered to the client. Continue?`}
        confirmText="Print"
        cancelText="Cancel"
        variant="danger"
        onConfirm={() => {
          setShowPrintConfirm(false)
          handleBulkPrintV2()
        }}
        onCancel={() => setShowPrintConfirm(false)}
      />

      <CustomModal
        isOpen={showPrintLimitModal}
        title="Too Many Bills Selected"
        handleClose={() => setShowPrintLimitModal(false)}
        maxWidth="sm"
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          You can only select up to {PRINT_SELECTION_LIMIT} bills for printing on this page.
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          If you need to print more, please use the{' '}
          <Link component={RouterLink} to="/billing/print-jobs" onClick={() => setShowPrintLimitModal(false)}>
            Print Jobs
          </Link>{' '}
          screen.
        </Typography>
        <DialogActions sx={{ padding: '20px 0 0', gap: 1 }}>
          <Button variant="outlined" onClick={() => setShowPrintLimitModal(false)}>
            Close
          </Button>
        </DialogActions>
      </CustomModal>
    </CardBox>
  )
}

export default FinalBills
